import {
  CheckCircleIcon,
  ChatCircleDotsIcon,
  CircleDashedIcon,
  CircleHalfIcon,
  ClockCountdownIcon,
  HandTapIcon,
  XCircleIcon,
  type Icon,
} from '@phosphor-icons/react';

import { t } from '@web/i18n';
import { twcn } from '@web/ui/core/utils';

import type { TaskDisplayStatus } from '@voll-intelligence/domain';

type Tone = 'gray' | 'brand' | 'green' | 'amber' | 'red' | 'ai';

const TONE_CLASS: Record<Tone, string> = {
  gray: 'text-muted-foreground',
  brand: 'text-primary',
  green: 'text-tone-green-fg',
  amber: 'text-tone-amber-fg',
  red: 'text-tone-red-fg',
  ai: 'text-ai',
};

/** Tone-tinted box for the `framed` avatar variant (drawer header). */
const FRAME_CLASS: Record<Tone, string> = {
  gray: 'bg-muted border-border-soft',
  brand: 'bg-primary-tint border-primary/22',
  green: 'bg-tone-green-tint border-tone-green-fg/22',
  amber: 'bg-tone-amber-tint border-tone-amber-fg/22',
  red: 'bg-tone-red-tint border-tone-red-fg/22',
  ai: 'bg-ai-tint border-ai/22',
};

/**
 * Task status glyph, shared by the Tarefas board and the drawer's Tasks
 * tab + task detail. Takes the already-derived {@link TaskDisplayStatus}
 * (see `deriveTaskDisplayStatus`) and maps it to a single icon + tone —
 * `running` spins via the `animate-spin-slow` keyframes. Pass `framed`
 * for the drawer header avatar: a tone-tinted rounded square.
 */
const ICON: Record<TaskDisplayStatus, { icon: Icon; tone: Tone; spins?: boolean }> = {
  pending: { icon: CircleDashedIcon, tone: 'gray' },
  running: { icon: CircleHalfIcon, tone: 'brand', spins: true },
  waiting_client: { icon: ChatCircleDotsIcon, tone: 'amber' },
  waiting_operation: { icon: ClockCountdownIcon, tone: 'gray' },
  waiting_approval: { icon: HandTapIcon, tone: 'ai' },
  failed: { icon: XCircleIcon, tone: 'red' },
  done: { icon: CheckCircleIcon, tone: 'green' },
};

/** i18n key per display status — drives the hover tooltip + `aria-label`. */
const LABEL_KEY = {
  pending: 'board.taskStatus.pending',
  running: 'board.taskStatus.running',
  waiting_client: 'board.taskStatus.waiting_client',
  waiting_operation: 'board.taskStatus.waiting_operation',
  waiting_approval: 'board.taskStatus.waiting_approval',
  failed: 'board.taskStatus.failed',
  done: 'board.taskStatus.done',
} as const satisfies Record<TaskDisplayStatus, string>;

export interface TaskStatusIconProps {
  status: TaskDisplayStatus;
  /** Wraps the glyph in a tone-tinted rounded square (drawer header avatar). */
  framed?: boolean;
  size?: number;
}

export function TaskStatusIcon({
  status,
  framed,
  size = 16,
}: Readonly<TaskStatusIconProps>): React.JSX.Element {
  const { icon: Cmp, tone, spins } = ICON[status];
  const label = t(LABEL_KEY[status]);
  const glyph = (
    <span
      className={twcn(
        'inline-flex items-center justify-center',
        TONE_CLASS[tone],
        spins && 'motion-safe:animate-spin-slow',
      )}
      title={label}
      aria-label={label}
      data-status={status}
    >
      <Cmp size={size} weight="regular" aria-hidden />
    </span>
  );

  if (!framed) return glyph;

  return (
    <span
      className={twcn(
        'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border',
        FRAME_CLASS[tone],
      )}
      title={label}
    >
      {glyph}
    </span>
  );
}
